import * as angular from 'angular';
import { Transition, TransitionService } from '@uirouter/angularjs';
import * as _ from 'lodash';

import {
    ComponentDefinition, Group, IApplicationConfiguration, IStringDateRange2, RefreshClickData, toDatePickerDates, ValidationPageConfiguration, ValidationScope,
    Notification,
    NotificationType
} from '../../models';
import { groupSelected, momentFormat, notificationReceived, refreshClick, Roles, treeViewNavigationEnd, treeViewNavigationStart } from '../../common';
import { IAuthorizationService, IDateHelperFormatService, IDateRangeService, IGroupEnforceAlignService, INotificationHubService, IRelativeGroupHelperService, IStoreService, IValidationPageConfigurationService } from '../../services';
import { NavigationMode } from '../group/navigationMode';
import { Store, StoreElement } from '../../stateManagement';
import { getGroup } from './getGroup';
import { IWidget } from '../../widgets';
import { IValidationStandAloneService } from './validationStandAlone.service';

class ValidationStandAloneController implements angular.IComponentController, angular.IOnInit, angular.IOnChanges {
    canLaunch: boolean;
    date: string;
    dates: IStringDateRange2;
    pickerDisabled:boolean=true;
    format: string;
    groupGuids: string[];
    groups: Group[];
    groupsDictionary: { [guid: string]: Group; };
    initialRange: IStringDateRange2;
    interval: { interval: number; unit: string };
    launchRange: IStringDateRange2;
    navigationMode: NavigationMode;
    needRefresh: {
        isRequired: boolean;
        message: string;
    };
    referenceGroup: string;
    selectedGroup: Group;
    share: any;
    store: Store;
    types: string[];
    widgetList: IWidget[]; //readonly, coming from underneath component

    readonly appConfiguration: IApplicationConfiguration;
    readonly group: Group;
    readonly isMobileMode: boolean;

    private _garbages: Function[] = [];
    private _validationPagesDictionary: { [guid: string]: ValidationPageConfiguration; };

    static $inject = [
        '$scope',
        '$rootScope',
        '$transitions',
        '$state',
        'authorizationService',
        'dateHelperFormatService',
        'dateRangeService',
        'groupEnforceAlignService',
        'storeService',
        'relativeGroupHelperService',
        'validationPageConfigurationService',
        'validationStandAloneService'
    ];

    constructor(
        private _$scope: angular.IScope,
        private _$rootScope: angular.IRootScopeService,
        private _$transitions: TransitionService,
        private _$state: angular.ui.IStateService,
        private _authorizationService: IAuthorizationService,
        private _dateHelperFormatService: IDateHelperFormatService,
        private _dateRangeService: IDateRangeService,
        private _alignService: IGroupEnforceAlignService,
        private _storeService: IStoreService,
        private _relativeGroupHelperService: IRelativeGroupHelperService,
        private _vpcs: IValidationPageConfigurationService,
        private _vsas: IValidationStandAloneService
    ) {
        this.format = this._dateHelperFormatService.getDateFormat();
    }

    $onInit(): void {
        this._$scope.$on(refreshClick, (_ev, arg) => {
            this.setDefaultRange(arg);
            this.needRefresh = null;
        });
        this._dateRangeService.initByState(this._$state.params);
        this.dates = {
            beginDate: this._dateRangeService.beginDate() as string,
            endDate: this._dateRangeService.endDate() as string
        };
        this.initialRange = this.dates;
        this.share = {};
        this.canLaunch = this._authorizationService.isInRoles([Roles.engineer, Roles.master]);
        this.initNavigationMode();
        this._storeService.register(
            store => this.initStore(store),
            [
                StoreElement.ValueViewModels,
                StoreElement.DigitalSets,
                StoreElement.Groups,
                StoreElement.ValidationPageConfigurations
            ]
        );
        this._garbages.push(
            this._$rootScope.$on(groupSelected, (_$event, group: Group) => {
                if (group?.guid !== this.selectedGroup?.guid) {
                    this.selectedGroup = group;
                }
            }),
            this._$transitions.onSuccess({ to: 'validation' }, (transition: Transition) => {
                const paramsChanged = transition.paramsChanged();
                const groupGuid = paramsChanged['groupGuid'];
                this.onChangeGroup(groupGuid);
            }),
            this._$rootScope.$on(notificationReceived, (_e, notification: Notification) => {
                if (notification.type === NotificationType.TaskCompleted && notification.payload?.results?.groupGuids) {
                    const groupGuids: string[] = notification.payload?.results['groupGuids'] as string[];
                    // Sub-optimal, could be done only when list of widgets changes; this works as a first approach
                    this._vsas.getGroupGuids(this.widgetList, this.dates, this.group, this.groupsDictionary, this.store.valueViewModels.dictionary, this.referenceGroup)
                        .then(currentGuids => {
                            const matches = groupGuids.filter(d => currentGuids.includes(d));
                            const nr = matches.length > 0;
                            this.needRefresh = {
                                isRequired: nr,
                                message: nr ? 'Click to see latest data.' : null
                            };
                        })
                        .catch(e => console.error(e));
                }
            }));

    }

    $onChanges(changes: angular.IOnChangesObject): void {
        if (changes['group']?.currentValue && this.selectedGroup?.guid !== changes['group']?.currentValue?.guid) {
            this.selectedGroup = changes['group'].currentValue;
            this.setReferenceGroup();
        }
        this.initNavigationMode();
        //#51102  
        //this.enforceDateRange();
    }

    $onDestroy() {
        this._garbages.forEach(g => g());
    }

    private initStore(store: Store): void {
        this.store = store;
        this.groups = _.values(store.groups.dictionary);
        this.groupsDictionary = store.groups.dictionary;
        this._validationPagesDictionary = store.validationPageConfigurations.dictionary;
        this.setReferenceGroup();
        this.setNavigationForGroupType(this.selectedGroup);
    }

    private onChangeGroup(groupGuid: string): void {
        if (groupGuid !== this.selectedGroup?.guid) {
            this._$rootScope.$broadcast(treeViewNavigationStart);
            getGroup(groupGuid, this._vpcs)
                .then(group => {
                    if (group && this.selectedGroup?.guid !== group.guid) {
                        this.setNavigationForGroupType(group);
                        this.selectedGroup = group;
                        this.setReferenceGroup();
                        this.initNavigationMode();
                        this.enforceDateRange();
                    }
                })
                .finally(() => this._$rootScope.$broadcast(treeViewNavigationEnd));
        }
        this.pickerDisabled=true;
    }

    private setReferenceGroup() {
        try {
            if (this._validationPagesDictionary) {
                this.referenceGroup = this._relativeGroupHelperService.getReferenceGroup(
                    this.selectedGroup,
                    this._validationPagesDictionary
                );
            }
        } catch (e) {
            console.error(e);
        }
    }

    private setNavigationForGroupType(group: Group) {
        if (group.type && group.configMode === 0 && this.groups) {
            this.groupGuids = this.groups
                .filter(g => g.type === group.type)
                .map(g => g.guid);
            this.types = [group.type];
        } else {
            this.groupGuids = [];
            this.types = [];
        }
    }

    private initNavigationMode(): void {
        if (this.selectedGroup) {
            this.navigationMode = this.selectedGroup.validationScope !== ValidationScope.MultiplePeriod
                ? NavigationMode.Single
                : NavigationMode.Double;
        }
    }

    private setDefaultRange(arg: RefreshClickData) {
        // #40541 : prevent wrong refreshing trough date picker of reprocess modal
        if (!arg.isLogbookEntryUpdated) {
            this.launchRange = arg;
        }
        this.pickerDisabled=true;
    }

    private enforceDateRange() {
        const isSinglePeriod = this.selectedGroup?.validationScope !== ValidationScope.MultiplePeriod;
        if (this.dates && this.selectedGroup && this.appConfiguration) {
            const aligned = this._alignService.groupEnforceAlign(
                toDatePickerDates(this.dates),
                this.selectedGroup.frequency,
                this.appConfiguration,
                isSinglePeriod
            );
            this.date = aligned.startDate.format(momentFormat);
            this.dates.beginDate = this.date;
        }
    }
}

export const wtValidationStandAlone: ComponentDefinition = {
    componentId: 'wtValidationStandAlone',
    controller: ValidationStandAloneController,
    template: require('./validationStandAlone.component.html'),
    controllerAs: 'vm',
    bindings: {
        appConfiguration: '<',
        group: '<',
        isMobileMode: '<?'
    }
};